import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Plus,
  Briefcase,
  Users,
  Upload,
  BarChart2,
  Archive,
  Trash2,
  ChevronRight,
  Clock,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { SkeletonCard } from '../../components/ui/LoadingSpinner'
import { UpgradeBanner, UpgradeModal } from '../../components/shared/UpgradeModal'
import { ConfirmModal } from '../../components/ui/Modal'
import useJobStore from '../../store/jobStore'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'
import { formatDate, timeAgo, formatNumber } from '../../lib/utils'
import { getEffectivePlan } from '../../lib/subscription'
import { useLocale } from '../../i18n/LocaleProvider'
import toast from 'react-hot-toast'

export function Dashboard() {
  const navigate = useNavigate()
  const { jobs, loadingJobs, fetchJobs, deleteJob, archiveJob } = useJobStore()
  const { user } = useAuthStore()
  const { org, usage, fetchUsage } = useOrgStore()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [statusFilter, setStatusFilter] = useState('active')
  const { locale, t } = useLocale()

  const plan = getEffectivePlan({ user, org, usage })
  const canManageJobs = user?.role === 'owner' || user?.isPlatformAdmin === true

  useEffect(() => {
    fetchJobs(statusFilter)
    fetchUsage()
  }, [statusFilter])

  async function handleDelete(job) {
    if (!canManageJobs) {
      toast.error(t('authenticated.dashboard.toasts.deleteForbidden'))
      return
    }

    setDeleting(true)
    const result = await deleteJob(job.jobId)
    if (result.success) {
      toast.success(t('authenticated.dashboard.toasts.deleted'))
    } else {
      toast.error(result.error || t('authenticated.dashboard.toasts.deleteFailed'))
    }
    setDeleting(false)
    setDeleteConfirm(null)
  }

  async function handleArchive(jobId) {
    if (!canManageJobs) {
      toast.error(t('authenticated.dashboard.toasts.archiveForbidden'))
      return
    }

    const result = await archiveJob(jobId)
    if (result.success) {
      toast.success(t('authenticated.dashboard.toasts.archived'))
      fetchJobs(statusFilter)
    } else {
      toast.error(result.error || t('authenticated.dashboard.toasts.archiveFailed'))
    }
  }

  function handleNewJob() {
    if (!canManageJobs) {
      toast.error(t('authenticated.dashboard.toasts.createForbidden'))
      return
    }

    if (usage?.canCreateJob === false) {
      setShowUpgradeModal(true)
      return
    }
    navigate('/dashboard/jobs/new')
  }

  const stats = [
    {
      label: t('authenticated.dashboard.activeJobs'),
      value: usage?.activeJobs ?? jobs.length,
      max: usage?.maxJobs,
      icon: Briefcase,
      color: 'text-brand-600 bg-brand-50',
    },
    {
      label: t('authenticated.dashboard.cvsThisMonth'),
      value: usage?.monthlyCVs ?? 0,
      max: usage?.maxCVsPerMonth,
      icon: Upload,
      color: 'text-green-600 bg-green-50',
    },
  ]

  return (
    <div className="animate-fade-in">
      {/* Upgrade banner */}
      {usage?.canCreateJob === false && canManageJobs && (
        <UpgradeBanner
          message={t('authenticated.dashboard.jobLimit', { plan })}
          onUpgrade={() => setShowUpgradeModal(true)}
        />
      )}

      {usage?.canUploadCV === false && (
        <UpgradeBanner
          message={t('authenticated.dashboard.cvLimit')}
          onUpgrade={() => setShowUpgradeModal(true)}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="stat-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  className={`w-9 h-9 rounded-xl ${stat.color} flex items-center justify-center`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                {stat.max && stat.max !== -1 && (
                  <span className="text-xs text-gray-400">
                    {t('authenticated.dashboard.ofMax', { max: formatNumber(stat.max) })}
                  </span>
                )}
              </div>
              <div className="stat-value">{formatNumber(stat.value)}</div>
              <div className="stat-label">{stat.label}</div>
              {stat.max && stat.max !== -1 && (
                <div className="mt-2 w-full bg-gray-100 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full transition-all ${
                      (stat.value / stat.max) >= 0.9
                        ? 'bg-red-500'
                        : (stat.value / stat.max) >= 0.7
                        ? 'bg-yellow-500'
                        : 'bg-brand-500'
                    }`}
                    style={{
                      width: `${Math.min(100, (stat.value / stat.max) * 100)}%`,
                    }}
                  />
                </div>
              )}
            </motion.div>
          )
        })}
      </div>

      {/* Jobs header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-gray-900">{t('authenticated.dashboard.jobOpenings')}</h2>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {['active', 'archived', 'closed'].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                  statusFilter === s
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t(`authenticated.dashboard.filters.${s}`)}
              </button>
            ))}
          </div>
        </div>
        {canManageJobs && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleNewJob}
            icon={<Plus className="w-4 h-4" />}
          >
            {t('authenticated.dashboard.newJob')}
          </Button>
        )}
      </div>

      {/* Jobs list */}
      {loadingJobs ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="card"
        >
          <div className="empty-state">
            <div className="empty-state-icon">
              <Briefcase className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="empty-state-title">
              {statusFilter === 'active'
                ? t('authenticated.dashboard.emptyActive')
                : t('authenticated.dashboard.emptyStatus', { status: t(`authenticated.dashboard.filters.${statusFilter}`).toLowerCase() })}
            </h3>
            <p className="empty-state-description">
              {statusFilter === 'active'
                ? canManageJobs
                  ? t('authenticated.dashboard.emptyActiveOwner')
                  : t('authenticated.dashboard.emptyActiveMember')
                : t('authenticated.dashboard.emptyStatusDescription', { status: t(`authenticated.dashboard.filters.${statusFilter}`).toLowerCase() })}
            </p>
            {statusFilter === 'active' && canManageJobs && (
              <Button
                variant="primary"
                className="mt-4"
                onClick={handleNewJob}
                icon={<Plus className="w-4 h-4" />}
              >
                {t('authenticated.dashboard.createFirstJob')}
              </Button>
            )}
          </div>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job, index) => (
            <motion.div
              key={job.jobId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card-hover p-5"
              onClick={() => navigate(`/dashboard/jobs/${job.jobId}`)}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
                    <Briefcase className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {job.title}
                      </h3>
                      <StatusBadge status={job.status} size="sm" />
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {t('authenticated.dashboard.candidates', {
                          count: job.candidateCount || 0,
                          plural: (job.candidateCount || 0) === 1 ? '' : 's',
                        })}
                      </span>
                      {job.topMatchScore > 0 && (
                        <span className="flex items-center gap-1">
                          <BarChart2 className="w-3.5 h-3.5" />
                          {t('authenticated.dashboard.topMatch', { score: job.topMatchScore })}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {timeAgo(job.createdAt, locale)}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className="flex items-center gap-2 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {canManageJobs && job.status === 'active' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleArchive(job.jobId)
                      }}
                      icon={<Archive className="w-3.5 h-3.5" />}
                    >
                      {t('authenticated.dashboard.archive')}
                    </Button>
                  )}
                  {canManageJobs && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteConfirm(job)
                      }}
                      icon={<Trash2 className="w-3.5 h-3.5 text-red-400" />}
                    />
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modals */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trigger={t('authenticated.dashboard.deleteTrigger')}
      />

      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
        title={t('authenticated.dashboard.deleteTitle')}
        message={t('authenticated.dashboard.deleteMessage', { title: deleteConfirm?.title || '' })}
        confirmLabel={t('authenticated.dashboard.delete')}
        variant="danger"
        loading={deleting}
      />
    </div>
  )
}
